import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ToastComponent } from '../../shared/toast/toast.component';
import { PoliticianService } from '../../services/politician.service';
import { AuthService } from '../../services/auth.service';
import { ClientService } from '../../services/client.service';
import { EsService } from '../../services/es.service';
import { RecordService } from '../../services/record.service';
import { MiscService } from '../../services/misc.service';
import { Politician } from '../../shared/models/politician.model';
import { Client } from '../../shared/models/client.model';
import { ee, perPage50, profilePics } from '../../shared/constants';
import { addDaysToDate, removeTooltipElement, formatNotes, saveFile, generateStatsCSV, scrollTo } from '../../shared/functions';
import { exportChart, generateBarChart } from '../../shared/chartFunctions';
import { EsSearchStats, TwitterSorts } from '../../shared/types';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-stakeholder-details',
  templateUrl: './stakeholder-details.component.html',
  styleUrls: ['./stakeholder-details.component.scss'],
  standalone: false
})
export class StakeholderDetailsComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private politicianService = inject(PoliticianService);
  private clientService = inject(ClientService);
  private esService = inject(EsService);
  private recordService = inject(RecordService);
  private miscService = inject(MiscService);
  toast = inject(ToastComponent);
  private route = inject(ActivatedRoute);
  private router = inject(Router);


  math = Math;
  isLoading = {
    politician: true,
    results: true,
    topWords: true,
    stats: true
  };
  isEditStakeholderDetailsModalVisible = false;
  field = '';
  currentYear = new Date().getFullYear();
  profilePics = profilePics;
  perPage = perPage50;
  formatNotes = formatNotes;
  exportChart = exportChart;
  id = '';
  politician: Politician;
  clients: Client[] = [];
  governments = [];
  legislatures = [];
  results = [];
  topWords = [];
  count = 0;
  page = 0;
  csvStats: EsSearchStats = {};
  barChartFollowers;
  barChartTopWords;
  followersDate = '90';
  twitterSort: TwitterSorts = 'date';
  currentTab = 'track-record';
  currentContent = 'track-record';
  menuItems = [
    { name: 'Info & Records', link: 'track-record', icon: 'list' },
  ];


  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.id = this.route.snapshot.paramMap.get('id');
    ee.on('changeContent', (data: string) => this.currentContent = data);
    ee.on('getRecords', (page?: number) => this.getRecords(this.id, page));
    ee.on('updatePolitician', (field: string) => this.toggleEditStakeholderDetailsModal(field));
    ee.on('getPolitician', () => this.getPolitician(this.id));
    this.getPolitician(this.id);
    this.getClients();
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getRecords', () => {});
    ee.off('updatePolitician', () => {});
    ee.off('getPolitician', () => {});
    this.barChartFollowers?.destroy();
    this.barChartTopWords?.destroy();
    removeTooltipElement();
  }

  getPolitician(id: string): void {
    this.isLoading.politician = true;
    this.politicianService.get(id)
      .pipe(finalize(() => {
        this.isLoading.politician = false;
        this.changeTab('track-record');
      }))
      .subscribe({
        next: res => this.politician = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getClients(): void {
    this.clientService.search({ active: '1' })
      .subscribe({
        next: res => this.clients = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  toggleEditStakeholderDetailsModal(field: string): void {
    this.isEditStakeholderDetailsModalVisible = true;
    this.field = field;
    ee.emit('showEditStakeholderDetailsModal', field);
  }

  getPoliticianActivities(person: string, source: string, page?: number): void {
    this.isLoading.results = true;
    this.page = page || 0;
    this.esService.search({ person, source, page, days: '90' })
      .pipe(finalize(() => {
        this.isLoading.results = false;
        this.currentTab = source;
      }))
      .subscribe({
        next: res => {
          this.results = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  nextPrevPage(currentTab: string, direction: 'next' | 'prev'): void {
    this.page = direction === 'next' ? this.page + 1 : this.page - 1;
    let person = this.generatePersonLastName();
    const username = this.politician.socials?.twitter?.username;
    switch (currentTab) {
      case 'track-record':
        this.getRecords(this.politician._id, this.page);
        break;
      case 'twitter':
        if (username) {
          person = username;
          this.getPoliticianActivities(person, currentTab, this.page);
        }
        break;
      case 'camera':
      case 'senato':
      case 'eu':
        this.getPoliticianActivities(person, currentTab, this.page);
        break;
    }
    scrollTo(300);
  }

  getRecords(id: string, page?: number): void {
    this.isLoading.results = true;
    this.page = page || 0;
    this.recordService.search({
      _politicians: id,
      page: this.page,
      perPage: this.perPage,
    })
      .pipe(finalize(() => {
        this.isLoading.results = false;
        this.currentTab = 'track-record';
      }))
      .subscribe({
        next: res => {
          this.results = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getTwitterStats(username: string): void {
    const startDate = addDaysToDate(new Date(), -+this.followersDate);
    forkJoin([
      this.recordService.getStatsByFollowers({ username, startDate }),
      this.esService.topWords({ person: username, source: 'twitter', days: '90' })
    ])
      .pipe(finalize(() => {
        this.isLoading.stats = false;
        this.isLoading.topWords = false;
        this.currentTab = 'twitter';
      }))
      .subscribe({
        next: ([statsByFollowers, topWords]) => {
          this.csvStats = { statsByFollowers, topWords };
          this.barChartFollowers = generateBarChart(statsByFollowers, '1', { label: 'followers', type: 'line' });
          this.barChartTopWords = generateBarChart(topWords, '2', { label: 'occurrences' });
        },
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  downloadCSV(what: string, header: string) {
    saveFile(generateStatsCSV(this.csvStats[what], header), `${what}.csv`);
  }

  generatePersonLastName(): string {
    const lastName = this.politician.lastName || '';
    return lastName.includes(' ') ? `"${lastName}"` : lastName;
  }

  changeTab(source: string): void {
    let person = this.generatePersonLastName();
    const username = this.politician.socials?.twitter?.username;
    this.twitterSort = 'date';
    switch (source) {
      case 'track-record':
        this.getRecords(this.politician._id);
        break;
      case 'twitter':
        this.barChartFollowers?.destroy();
        this.barChartTopWords?.destroy();
        if (username) {
          person = username;
          this.getTwitterStats(person);
          // this.getTwitterTweets(username); // Commented since new Twitter limits
          this.getPoliticianActivities(person, source);
        }
        break;
      case 'camera':
      case 'senato':
      case 'eu':
        this.getPoliticianActivities(person, source);
        break;
    }
  }

  getTwitterTweets(username: string): void {
    this.isLoading.results = true;
    this.miscService.twitterTweets(username, this.twitterSort)
      .pipe(finalize(() => this.isLoading.results = false))
      .subscribe({
        next: res => {
          this.results = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  changeTwitterSort(): void {
    const username = this.politician.socials?.twitter?.username;
    this.getTwitterTweets(username);
  }

  changeFollowersDateRange(): void {
    this.barChartFollowers?.destroy();
    const username = this.politician.socials?.twitter?.username;
    this.getTwitterStats(username);
  }

}
